import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { clearAuthToken } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  LogOut, Settings, UserPlus, Play, Eye,
  Users, Trophy, Target, TrendingUp, Loader2, Plus,
  Calendar, ChevronRight, ChevronDown, ChevronUp,
  BarChart3, Copy, Check, Pencil, Info,
  Dumbbell, ClipboardList, UserCheck
} from 'lucide-react';
import EditProfileModal from '@/components/EditProfileModal';
import EditFanPlayerModal from '@/components/EditFanPlayerModal';
import GameAnalysisModal, { buildAnalysisInputs, generatePlayerAnalysis } from '@/components/GameAnalysisModal';
import ForwardFocus from '@/components/ForwardFocus';
import { ThemeProvider } from '@/context/ThemeContext';

function FanDashboardContent({ user, insideLayout }) {
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);
  const [players, setPlayers] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [allPlayers, setAllPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerStats, setPlayerStats] = useState(null);
  const [allTimeStats, setAllTimeStats] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showNewGame, setShowNewGame] = useState(false);
  const [showManualSetup, setShowManualSetup] = useState(false);
  const [showAllTimeStats, setShowAllTimeStats] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showJoinTeam, setShowJoinTeam] = useState(false);
  const [showLinkPlayer, setShowLinkPlayer] = useState(false);
  const [showAthleteSettings, setShowAthleteSettings] = useState(false);
  const [showEditPlayer, setShowEditPlayer] = useState(false);
  const [lastPracticeSession, setLastPracticeSession] = useState(null);
  const [teamFilter, setTeamFilter] = useState('all');
  const [seasonFilter, setSeasonFilter] = useState('all');
  const [analysisGame, setAnalysisGame] = useState(null);
  const navigate = useNavigate();

  const tc = currentUser?.theme_color || '#7e22ce';
  const tcLight = tc + '15';

  useEffect(() => {
    fetchPlayers();
    fetchAllTimeStats();
  }, []);

  useEffect(() => {
    if (selectedPlayer) {
      fetchPlayerStats(selectedPlayer.player_id);
      fetchGames(selectedPlayer.player_id);
      fetchLastPractice(selectedPlayer.player_id);
      setTeamFilter('all');
    } else {
      setPlayerStats(null);
      setGames([]);
      setLastPracticeSession(null);
    }
  }, [selectedPlayer]);

  const fetchPlayers = async () => {
    try {
      const response = await api.get('/fan/players');
      setPlayers(response.data || []);
      const allResponse = await api.get('/fan/players?include_archived=true');
      setAllPlayers(allResponse.data || []);
      if (response.data?.length > 0 && !selectedPlayer) {
        setSelectedPlayer(response.data[0]);
      }
      if (selectedPlayer && !response.data?.find(p => p.player_id === selectedPlayer.player_id)) {
        setSelectedPlayer(response.data?.length > 0 ? response.data[0] : null);
      }
    } catch (error) {
      console.error('Failed to fetch players:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTimeStats = async () => {
    try {
      const response = await api.get('/fan/stats/all-time');
      setAllTimeStats(response.data);
    } catch (error) {
      console.error('Failed to fetch all-time stats:', error);
    }
  };

  const fetchPlayerStats = async (playerId) => {
    setStatsLoading(true);
    try {
      const response = await api.get(`/fan/players/${playerId}/stats`);
      setPlayerStats(response.data);
    } catch (error) {
      console.error('Failed to fetch player stats:', error);
      setPlayerStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchGames = async (playerId) => {
    try {
      const response = await api.get(`/fan/games?player_id=${playerId}`);
      setGames(response.data || []);
    } catch (error) {
      console.error('Failed to fetch games:', error);
    }
  };

  const fetchLastPractice = async (playerId) => {
    try {
      const response = await api.get(`/fan/players/${playerId}/practice-sessions?limit=1`);
      if (response.data && response.data.length > 0) {
        setLastPracticeSession(response.data[0]);
      } else {
        setLastPracticeSession(null);
      }
    } catch {
      setLastPracticeSession(null);
    }
  };

  const handlePlayerUpdated = (updatedPlayer) => {
    setSelectedPlayer(updatedPlayer);
    fetchPlayers();
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout', {});
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      clearAuthToken();
      navigate('/login');
    }
  };

  const handlePlayerAdded = (newPlayer) => {
    setShowAddPlayer(false);
    fetchPlayers();
    fetchAllTimeStats();
    setSelectedPlayer(newPlayer);
  };

  const handleGameCreated = (newGame) => {
    setShowNewGame(false);
    if (selectedPlayer) fetchGames(selectedPlayer.player_id);
    fetchAllTimeStats();
    navigate(`/fan/game/${newGame.game_id}`);
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success('Copied!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Compute unique teams and seasons from playerStats
  const teamNames = useMemo(() => {
    if (!playerStats?.recent_games) return [];
    const names = [...new Set(playerStats.recent_games.map(g => g.team_name).filter(Boolean))];
    return names.sort();
  }, [playerStats]);

  const seasonNames = useMemo(() => {
    if (!playerStats?.recent_games) return [];
    const names = [...new Set(playerStats.recent_games.map(g => g.season_name).filter(Boolean))];
    return names.sort();
  }, [playerStats]);

  const filteredStats = useMemo(() => {
    if (!playerStats || !playerStats.recent_games) return playerStats;
    if (teamFilter === 'all' && seasonFilter === 'all') return playerStats;

    const filtered = playerStats.recent_games.filter(g => {
      if (teamFilter !== 'all' && g.team_name !== teamFilter) return false;
      if (seasonFilter !== 'all' && g.season_name !== seasonFilter) return false;
      return true;
    });
    if (filtered.length === 0) return { ...playerStats, games_played: 0, recent_games: [], totals: {}, averages: {} };

    const keys = ["pts","fgm","fga","three_pm","three_pa","ftm","fta","oreb","dreb","reb","ast","stl","blk","defl","to","pf"];
    const totals = {};
    keys.forEach(k => { totals[k] = filtered.reduce((sum, g) => sum + (g[k] || 0), 0); });
    const n = filtered.length;
    const averages = {};
    keys.forEach(k => { averages[k] = n > 0 ? Math.round((totals[k] / n) * 10) / 10 : 0; });
    if (totals.fga > 0) averages.fg_pct = Math.round((totals.fgm / totals.fga) * 1000) / 10;
    if (totals.three_pa > 0) averages.three_p_pct = Math.round((totals.three_pm / totals.three_pa) * 1000) / 10;
    if (totals.fta > 0) averages.ft_pct = Math.round((totals.ftm / totals.fta) * 1000) / 10;

    return { ...playerStats, games_played: n, recent_games: filtered, totals, averages };
  }, [playerStats, teamFilter, seasonFilter]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Forward Focus: analysis from most recent completed game
  const forwardFocusData = useMemo(() => {
    if (!filteredStats?.recent_games?.length || !playerStats?.averages) return null;
    // Include any game with stats — fan-tracked games may lack a status
    const gamesWithStats = filteredStats.recent_games
      .filter(g => g.pts != null || g.fgm != null)
      .sort((a, b) => new Date(b.date_time || 0) - new Date(a.date_time || 0));
    if (gamesWithStats.length === 0) return null;

    const lastGame = gamesWithStats[0];
    const ats = playerStats.averages;
    try {
      const inputs = buildAnalysisInputs(lastGame, { averages: ats, totals: playerStats.totals || {} });
      const analysis = generatePlayerAnalysis(inputs);
      if (!analysis || (analysis.wins.length === 0 && analysis.opportunities.length === 0)) return null;
      return {
        wins: analysis.wins,
        opportunities: analysis.opportunities,
        practiceFocus: analysis.practice_focus || [],
        gameLabel: lastGame.opponent_name
          ? `vs ${lastGame.opponent_name} · ${formatDate(lastGame.date_time)}`
          : lastGame.date_time ? formatDate(lastGame.date_time) : 'Last Game',
        gameId: lastGame.game_id
      };
    } catch {
      return null;
    }
  }, [filteredStats, playerStats]);

  if (loading) {
    return (
      <div className={insideLayout ? "flex items-center justify-center py-20" : "min-h-screen flex items-center justify-center bg-white"}>
        <div className="animate-spin h-12 w-12 border-4 border-slate-900 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // No players yet - onboarding
  if (players.length === 0) {
    return (
      <div className={insideLayout ? "" : "min-h-screen bg-gradient-to-b from-white to-slate-50"}>
        {!insideLayout && <Header user={currentUser} onLogout={handleLogout} onEditProfile={() => setShowEditProfile(true)} />}
        <div className="max-w-2xl mx-auto px-4 pt-5 pb-2">
          <h2 className="text-xl font-semibold text-slate-800">Welcome, {currentUser?.name || 'Fan'}</h2>
          <span className="inline-block mt-1 text-xs px-3 py-0.5 rounded-full font-bold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif", backgroundColor: tcLight, color: tc }} data-testid="user-role-badge">Fan</span>
        </div>
        <div className="max-w-2xl mx-auto p-4 md:p-8">
          <div className="bg-white border border-slate-200 rounded-lg shadow-md p-8 text-center">
            <div className="inline-block p-4 bg-slate-100 rounded-full mb-4">
              <Users className="w-12 h-12 text-slate-400" />
            </div>
            <h2 className="text-2xl font-bold font-heading uppercase tracking-tight text-slate-900 mb-2" data-testid="welcome-heading">Player Management Center</h2>
            <p className="text-slate-600 mb-8">Get started by adding a new player or linking to your child's existing account.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={() => setShowAddPlayer(true)}
                data-testid="create-first-player-button"
                className="h-12 px-8 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide w-full sm:w-auto"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                Add New Player
              </Button>
              <Button
                onClick={() => setShowLinkPlayer(true)}
                variant="outline"
                data-testid="link-first-player-button"
                className="h-12 px-8 font-bold uppercase tracking-wide w-full sm:w-auto border-2"
              >
                <Users className="w-5 h-5 mr-2" />
                Link Existing Player
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-4">Already have a player with their own account? Use "Link Existing Player" and enter their Athlete ID.</p>
          </div>
        </div>
        <AddPlayerModal open={showAddPlayer} onOpenChange={setShowAddPlayer} onPlayerAdded={handlePlayerAdded} themeColor={tc} />
        <LinkPlayerModal open={showLinkPlayer} onOpenChange={setShowLinkPlayer} players={players} themeColor={tc} onSuccess={fetchPlayers} allowDirectLink />
        {!insideLayout && <EditProfileModal open={showEditProfile} onOpenChange={setShowEditProfile} user={currentUser} onSuccess={(u) => setCurrentUser(u)} />}
      </div>
    );
  }

  return (
    <div className={insideLayout ? "" : "min-h-screen bg-gradient-to-b from-white to-slate-50"}>
      {!insideLayout && <Header user={currentUser} onLogout={handleLogout} onEditProfile={() => setShowEditProfile(true)} />}

      {/* Welcome Section */}
      <div className="max-w-7xl mx-auto px-4 pt-5 pb-2">
        <h2 className="text-xl font-semibold text-slate-800">Welcome, {currentUser?.name || 'Fan'}</h2>
        <span className="inline-block mt-1 text-xs px-3 py-0.5 rounded-full font-bold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif", backgroundColor: tcLight, color: tc }} data-testid="user-role-badge">Fan</span>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Player Switcher Bar */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 mb-6" data-testid="player-switcher-bar">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p className="text-sm font-bold uppercase tracking-wider text-slate-900 mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Player (Active)</p>
              <div className="flex items-center gap-3">
                <div className="min-w-[220px]">
                  <Select
                    value={selectedPlayer?.player_id || ''}
                    onValueChange={(val) => {
                      const player = players.find(p => p.player_id === val);
                      if (player) setSelectedPlayer(player);
                    }}
                  >
                    <SelectTrigger data-testid="player-selector" className="h-10 font-medium">
                      <SelectValue placeholder="Select player" />
                    </SelectTrigger>
                    <SelectContent>
                      {players.map((p) => (
                        <SelectItem key={p.player_id} value={p.player_id}>
                          #{p.jersey_number || '?'} {p.name} &middot; {p.team_name} ({p.season})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      data-testid="manage-players-menu"
                      className="bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide h-10 px-4 gap-1.5"
                    >
                      <Plus className="w-4 h-4" /> Manage
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={() => setShowAddPlayer(true)} data-testid="menu-add-new-player" className="cursor-pointer py-3">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add New Player
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowLinkPlayer(true)} data-testid="menu-link-existing-player" className="cursor-pointer py-3">
                      <Users className="w-4 h-4 mr-2" />
                      Link Existing Player
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Gear icon for athlete settings */}
            <div className="flex items-center gap-2">
              {selectedPlayer && (
                <Button
                  onClick={() => setShowAthleteSettings(true)}
                  variant="ghost"
                  data-testid="athlete-settings-button"
                  className="h-10 px-3"
                  title="Athlete Settings"
                >
                  <Settings className="w-5 h-5 text-slate-500" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {selectedPlayer && (
          <>
            {/* Forward Focus — analysis from most recent game */}
            {forwardFocusData && (
              <ForwardFocus
                wins={forwardFocusData.wins}
                opportunities={forwardFocusData.opportunities}
                practiceFocus={forwardFocusData.practiceFocus}
                gameLabel={forwardFocusData.gameLabel}
                onViewGame={() => navigate(`/game/${forwardFocusData.gameId}/summary`)}
              />
            )}

            {/* Two-Card Layout — mirrors PlayerDashboard */}
            {/* Mobile: Single card */}
            <div className="md:hidden bg-white border border-slate-200 rounded-xl shadow-sm mb-6 p-5 relative">
              {/* Edit button */}
              <button
                onClick={() => setShowEditPlayer(true)}
                className="absolute top-3 right-3 p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="edit-player-btn-mobile"
                title="Edit player profile"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-4">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 border-4 border-white shadow-lg overflow-hidden"
                  style={{ backgroundColor: tc + '15', color: tc }}
                >
                  {selectedPlayer.profile_photo_url ? (
                    <img src={selectedPlayer.profile_photo_url} alt={selectedPlayer.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-2xl font-bold">{selectedPlayer.jersey_number || selectedPlayer.name?.charAt(0) || '#'}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-bold font-heading uppercase tracking-tight text-slate-900 truncate" data-testid="player-name">
                    {selectedPlayer.name}
                  </h2>
                  <p className="text-slate-500 text-sm">{selectedPlayer.team_name} &middot; #{selectedPlayer.jersey_number || '--'}</p>
                </div>
              </div>
              {/* Mobile action buttons */}
              <div className="grid grid-cols-3 gap-2 mt-4">
                <button onClick={() => navigate('/practice')} data-testid="action-practice" className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-emerald-600 text-white text-xs font-bold">
                  <Target className="w-3.5 h-3.5" /> Practice
                </button>
                <button onClick={() => setShowAllTimeStats(true)} data-testid="action-all-time-stats" className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border border-blue-300 text-blue-600 text-xs font-bold bg-white">
                  <BarChart3 className="w-3.5 h-3.5" /> All-Time
                </button>
                <button onClick={() => setShowJoinTeam(true)} data-testid="action-join-team-mobile" className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-purple-200 text-purple-800 text-xs font-bold">
                  Join Team
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button onClick={() => setShowNewGame(true)} data-testid="action-track-game" className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-blue-500 text-white text-xs font-bold">
                  Track Game
                </button>
                <button onClick={() => setShowManualSetup(true)} data-testid="action-add-stats-mobile" className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-blue-500 text-white text-xs font-bold">
                  Add Stats
                </button>
              </div>
            </div>

            {/* Desktop: Two Adjacent Cards — mirrors PlayerDashboard exactly */}
            <div className="hidden md:grid md:grid-cols-2 gap-4 mb-6">
              {/* Left Card — Player Info + Last Practice */}
              <div className="relative bg-white border border-slate-200 rounded-xl shadow-sm p-6 flex flex-col" data-testid="player-info-card">
                {/* Edit button */}
                <button
                  onClick={() => setShowEditPlayer(true)}
                  className="absolute top-3 right-3 p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                  data-testid="edit-player-btn"
                  title="Edit player profile"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                {/* Top Half — Player Info */}
                <div className="flex items-center gap-5 pb-4 border-b border-slate-100">
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center flex-shrink-0 border-4 border-white shadow-lg overflow-hidden"
                    style={{ backgroundColor: tc + '15', color: tc }}
                  >
                    {selectedPlayer.profile_photo_url ? (
                      <img src={selectedPlayer.profile_photo_url} alt={selectedPlayer.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-3xl font-bold">{selectedPlayer.jersey_number || selectedPlayer.name?.charAt(0) || '#'}</span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl font-bold font-heading uppercase tracking-tight text-slate-900 truncate" data-testid="player-name">
                      {selectedPlayer.name}
                    </h2>
                    <p className="text-slate-500 text-sm mt-0.5">{selectedPlayer.team_name} &middot; #{selectedPlayer.jersey_number || '--'}</p>
                  </div>
                </div>

                {/* Bottom Half — Last Practice */}
                <div className="pt-4 flex-1 flex flex-col justify-center">
                  {lastPracticeSession ? (
                    <button
                      onClick={() => navigate('/practice')}
                      className="w-full text-left hover:bg-slate-50 rounded-lg p-3 -m-3 transition-colors group"
                      data-testid="last-practice-link"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-slate-500 text-xs">
                          <Target className="w-3.5 h-3.5 text-emerald-600" />
                          <span className="font-semibold uppercase tracking-wider">Last Practice</span>
                        </div>
                        <span className="text-xs text-slate-400">
                          {new Date(lastPracticeSession.started_at || lastPracticeSession.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-center bg-slate-50 rounded-lg py-2 group-hover:bg-slate-100 transition-colors">
                          <p className="text-lg font-bold text-slate-900">
                            {lastPracticeSession.two_pt_attempted > 0
                              ? `${Math.round((lastPracticeSession.two_pt_made / lastPracticeSession.two_pt_attempted) * 100)}%`
                              : '-'}
                          </p>
                          <p className="text-[10px] text-slate-400 uppercase">2PT <span className="text-slate-300">({lastPracticeSession.two_pt_made}/{lastPracticeSession.two_pt_attempted})</span></p>
                        </div>
                        <div className="text-center bg-slate-50 rounded-lg py-2 group-hover:bg-slate-100 transition-colors">
                          <p className="text-lg font-bold text-slate-900">
                            {lastPracticeSession.three_pt_attempted > 0
                              ? `${Math.round((lastPracticeSession.three_pt_made / lastPracticeSession.three_pt_attempted) * 100)}%`
                              : '-'}
                          </p>
                          <p className="text-[10px] text-slate-400 uppercase">3PT <span className="text-slate-300">({lastPracticeSession.three_pt_made}/{lastPracticeSession.three_pt_attempted})</span></p>
                        </div>
                        <div className="text-center bg-slate-50 rounded-lg py-2 group-hover:bg-slate-100 transition-colors">
                          <p className="text-lg font-bold text-slate-900">
                            {lastPracticeSession.ft_attempted > 0
                              ? `${Math.round((lastPracticeSession.ft_made / lastPracticeSession.ft_attempted) * 100)}%`
                              : '-'}
                          </p>
                          <p className="text-[10px] text-slate-400 uppercase">FT <span className="text-slate-300">({lastPracticeSession.ft_made}/{lastPracticeSession.ft_attempted})</span></p>
                        </div>
                      </div>
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate('/practice')}
                      className="w-full text-left hover:bg-slate-50 rounded-lg p-3 -m-3 transition-colors"
                      data-testid="start-practice-link"
                    >
                      <div className="flex items-center gap-2 text-slate-400">
                        <Target className="w-4 h-4" />
                        <span className="text-sm">No practice sessions yet</span>
                        <ChevronRight className="w-4 h-4 ml-auto" />
                      </div>
                    </button>
                  )}
                </div>
              </div>

              {/* Right Card — Player Management Center */}
              <div className="relative bg-white border border-slate-200 rounded-xl shadow-sm p-6 flex flex-col" data-testid="management-center-card">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-sm font-bold font-heading uppercase tracking-wider text-slate-900" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    Player Management Center
                  </h3>
                  <button
                    onClick={() => setShowInfoModal(true)}
                    className="p-1 text-slate-900 hover:text-slate-700 transition-colors"
                    data-testid="info-button"
                    title="Learn more"
                  >
                    <Info className="w-6 h-6" strokeWidth={2.5} />
                  </button>
                </div>

                {/* Row 1: Practice | All-Time Stats | Join Team */}
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <button
                    onClick={() => navigate('/practice')}
                    data-testid="action-practice"
                    className="flex items-center justify-center gap-2 px-4 py-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-colors"
                  >
                    <Target className="w-4 h-4" />
                    Practice
                  </button>
                  <button
                    onClick={() => setShowAllTimeStats(true)}
                    data-testid="action-all-time-stats"
                    className="flex items-center justify-center gap-2 px-4 py-4 rounded-lg border-2 border-blue-300 bg-white hover:bg-blue-50 text-blue-600 font-bold text-sm transition-colors"
                  >
                    <BarChart3 className="w-4 h-4" />
                    All-Time Stats
                  </button>
                  <button
                    onClick={() => setShowJoinTeam(true)}
                    data-testid="action-join-team"
                    className="flex items-center justify-center gap-2 px-4 py-4 rounded-lg bg-purple-200 hover:bg-purple-300 text-purple-800 font-bold text-sm transition-colors"
                  >
                    Join Team
                  </button>
                </div>

                {/* Row 2: Track Game (Live) | Add Stats (Manual) */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setShowNewGame(true)}
                    data-testid="action-track-game"
                    className="flex items-center justify-center gap-2 px-4 py-4 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-bold text-sm transition-colors"
                  >
                    Track Game (Live)
                  </button>
                  <button
                    onClick={() => setShowManualSetup(true)}
                    data-testid="action-add-stats"
                    className="flex items-center justify-center gap-2 px-4 py-4 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-bold text-sm transition-colors"
                  >
                    Add Stats (Manual)
                  </button>
                </div>
              </div>
            </div>

            {/* Stats Section */}
            {statsLoading ? (
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-10 text-center mb-6">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
              </div>
            ) : filteredStats && filteredStats.games_played > 0 ? (
              <>
                {/* Team & Season Filters */}
                {(teamNames.length > 1 || seasonNames.length > 1) && (
                  <div className="flex items-center gap-3 mb-4" data-testid="game-filter-bar">
                    {teamNames.length > 1 && (
                      <Select value={teamFilter} onValueChange={setTeamFilter}>
                        <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="team-filter-dropdown">
                          <SelectValue placeholder="All Teams" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Teams ({playerStats?.recent_games?.length || 0})</SelectItem>
                          {teamNames.map(tn => (
                            <SelectItem key={tn} value={tn}>{tn}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {seasonNames.length > 1 && (
                      <Select value={seasonFilter} onValueChange={setSeasonFilter}>
                        <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="season-filter-dropdown">
                          <SelectValue placeholder="All Seasons" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Seasons</SelectItem>
                          {seasonNames.map(sn => (
                            <SelectItem key={sn} value={sn}>{sn}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                {/* Stats Cards — Row 1: PTS | AST | REB | STL */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
                  <StatCard label="PPG" value={filteredStats.averages.pts} />
                  <StatCard label="APG" value={filteredStats.averages.ast} />
                  <StatCard label="RPG" value={filteredStats.averages.reb} />
                  <StatCard label="STL" value={filteredStats.averages.stl} />
                </div>

                {/* Stats Cards — Row 2: BLK | 3PM | TO | FG% */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
                  <StatCard label="BLK" value={filteredStats.averages.blk} />
                  <StatCard label="3PM" value={filteredStats.averages.three_pm} />
                  <StatCard label="TO" value={filteredStats.averages.to} negative />
                  {filteredStats.averages.fg_pct !== undefined && (
                    <StatCard label="FG%" value={filteredStats.averages.fg_pct} suffix="%" />
                  )}
                </div>

                {/* Game Log */}
                <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">
                      Games
                    </h2>
                  </div>
                  {filteredStats.recent_games && filteredStats.recent_games.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      {filteredStats.recent_games.map((game, idx) => (
                        <div
                          key={idx}
                          className="p-4 hover:bg-slate-50 transition-colors"
                          data-testid={`game-history-${game.game_id || idx}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3 min-w-0 flex-1">
                              <div className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center font-bold text-white text-sm ${game.won ? 'bg-emerald-500' : 'bg-red-500'}`}>
                                {game.won ? 'W' : 'L'}
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-slate-900 text-sm truncate">
                                  {game.is_home ? 'vs' : '@'} {game.opponent}
                                  {game.source === 'coach' && (
                                    <span className="ml-1.5 text-xs font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">Team</span>
                                  )}
                                </p>
                                <p className="text-xs text-slate-500 truncate">
                                  {formatDate(game.date)} &middot; {game.home_score}-{game.away_score}
                                  {game.team_name && <span> &middot; {game.team_name}</span>}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 shrink-0 ml-3">
                              <div className="text-center">
                                <p className="text-lg font-bold text-slate-900">{game.pts}</p>
                                <p className="text-[10px] text-slate-500 uppercase">PTS</p>
                              </div>
                              <div className="text-center hidden sm:block">
                                <p className="text-lg font-bold text-slate-900">{game.reb}</p>
                                <p className="text-[10px] text-slate-500 uppercase">REB</p>
                              </div>
                              <div className="text-center hidden sm:block">
                                <p className="text-lg font-bold text-slate-900">{game.ast}</p>
                                <p className="text-[10px] text-slate-500 uppercase">AST</p>
                              </div>
                              <button
                                onClick={() => setAnalysisGame(game)}
                                className="hidden sm:inline-flex px-3 py-1.5 text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap"
                                data-testid={`game-analysis-btn-${game.game_id || idx}`}
                              >
                                Game Analysis & Practice Plan
                              </button>
                            </div>
                          </div>
                          {/* Mobile: compact row with REB, AST and analysis button */}
                          <div className="flex sm:hidden items-center justify-between mt-2 pl-12">
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-slate-500"><span className="font-bold text-slate-700">{game.reb}</span> REB</span>
                              <span className="text-xs text-slate-500"><span className="font-bold text-slate-700">{game.ast}</span> AST</span>
                            </div>
                            <button
                              onClick={() => setAnalysisGame(game)}
                              className="px-2 py-1 text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors"
                              data-testid={`game-analysis-btn-mobile-${game.game_id || idx}`}
                            >
                              Analysis
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-slate-500">No game history yet</div>
                  )}
                </div>
              </>
            ) : (
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8 text-center">
                <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-4">No games recorded yet for this player</p>
                <Button onClick={() => setShowNewGame(true)} className="bg-blue-500 hover:bg-blue-600 text-white">
                  <Play className="w-4 h-4 mr-2" /> Start First Game
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <AddPlayerModal open={showAddPlayer} onOpenChange={setShowAddPlayer} onPlayerAdded={handlePlayerAdded} themeColor={tc} />
      <NewGameModal open={showNewGame} onOpenChange={setShowNewGame} player={selectedPlayer} onGameCreated={handleGameCreated} themeColor={tc} />
      <ManualGameSetupModal open={showManualSetup} onOpenChange={setShowManualSetup} player={selectedPlayer} themeColor={tc} />
      <JoinTeamModal open={showJoinTeam} onOpenChange={setShowJoinTeam} selectedPlayer={selectedPlayer} players={players} themeColor={tc} onSuccess={fetchPlayers} />
      <LinkPlayerModal open={showLinkPlayer} onOpenChange={setShowLinkPlayer} players={players} themeColor={tc} onSuccess={fetchPlayers} />
      <AthleteSettingsModal open={showAthleteSettings} onOpenChange={setShowAthleteSettings} player={selectedPlayer} themeColor={tc} />
      <EditFanPlayerModal open={showEditPlayer} onOpenChange={setShowEditPlayer} player={selectedPlayer} onSuccess={handlePlayerUpdated} />
      <AllTimeStatsModal open={showAllTimeStats} onOpenChange={setShowAllTimeStats} stats={allTimeStats} themeColor={tc} />

      <GameAnalysisModal
        open={!!analysisGame}
        onOpenChange={(open) => { if (!open) setAnalysisGame(null); }}
        game={analysisGame}
        ats={playerStats || {}}
        opponentName={analysisGame?.opponent}
        gameDate={analysisGame?.date ? formatDate(analysisGame.date) : ''}
      />
      <InfoModal open={showInfoModal} onOpenChange={setShowInfoModal} />
      {!insideLayout && (
      <EditProfileModal
        open={showEditProfile}
        onOpenChange={setShowEditProfile}
        user={currentUser}
        onSuccess={(u) => setCurrentUser(u)}
        onThemeChange={(c) => setCurrentUser(prev => ({ ...prev, theme_color: c }))}
      />
      )}
    </div>
  );
}

export default function FanDashboard({ user, insideLayout }) {
  return (
    <ThemeProvider user={user} team={null}>
      <FanDashboardContent user={user} insideLayout={insideLayout} />
    </ThemeProvider>
  );
}

// ─── Sub Components ─────────────────────────────────────

function Header({ user, onLogout, onEditProfile }) {
  return (
    <div className="sticky top-0 z-40" style={{ backgroundColor: '#13314b' }}>
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4" style={{ width: '15%', minWidth: '100px' }}>
            <img src="/smarter-hoops-wordmark.png" alt="Smarter Hoops" className="w-full h-auto" />
          </div>
          <div className="flex items-center space-x-1 sm:space-x-2">
            <Button onClick={onEditProfile} variant="ghost" data-testid="edit-profile-button" className="text-white/70 hover:text-white hover:bg-white/10 px-2">
              <Settings className="w-5 h-5" />
            </Button>
            <Button onClick={onLogout} variant="ghost" data-testid="logout-button" className="text-white/70 hover:text-white hover:bg-white/10 px-2">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, negative, suffix = '' }) {
  return (
    <div className="rounded-[10px] p-4" style={{ background: '#fff', border: '1.5px solid #DDE5EC' }}>
      <p className="text-[14px] font-bold tracking-[.16em] uppercase mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#8FA3B1' }}>{label}</p>
      <p className={`text-[28px] font-bold tracking-tight leading-none ${negative ? 'text-orange-600' : ''}`}
        style={{ fontFamily: "'Barlow Condensed', sans-serif", color: negative ? '#E67E22' : '#0D1B26' }}>
        {value !== null && value !== undefined ? `${typeof value === 'number' ? value.toFixed(1) : value}${suffix}` : '0.0'}
      </p>
    </div>
  );
}

// ─── Modals ─────────────────────────────────────────────

function InfoModal({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="info-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-bold">
            <Info className="w-5 h-5 text-blue-600" />
            Player Management Center
          </DialogTitle>
          <DialogDescription className="text-slate-500">How to use your dashboard</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2 text-sm text-slate-700">
          <div>
            <h4 className="font-bold text-slate-900 mb-1 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-emerald-600 text-white text-xs font-bold">1</span>
              Practice
            </h4>
            <p>Log shooting practice sessions to track 2PT, 3PT, and FT percentages over time.</p>
          </div>
          <div>
            <h4 className="font-bold text-slate-900 mb-1 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded border-2 border-blue-300 text-blue-600 text-xs font-bold">2</span>
              All-Time Stats
            </h4>
            <p>View career statistics aggregated across all teams and seasons.</p>
          </div>
          <div>
            <h4 className="font-bold text-slate-900 mb-1 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-purple-200 text-purple-800 text-xs font-bold">3</span>
              Join Team
            </h4>
            <p>Connect your player to an official team using a join code from a coach.</p>
          </div>
          <div>
            <h4 className="font-bold text-slate-900 mb-1 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-blue-500 text-white text-xs font-bold">4</span>
              Track Game (Live)
            </h4>
            <p>Track stats in real-time during a game — makes, misses, rebounds, assists, and more.</p>
          </div>
          <div>
            <h4 className="font-bold text-slate-900 mb-1 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-blue-500 text-white text-xs font-bold">5</span>
              Add Stats (Manual)
            </h4>
            <p>Manually enter game stats after the game is over.</p>
          </div>
          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-400">Your <strong>Athlete ID</strong> is a unique identifier for your player. Share it with coaches or use it to connect across platforms.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddPlayerModal({ open, onOpenChange, onPlayerAdded, themeColor }) {
  const tc = themeColor || '#7e22ce';
  const [name, setName] = useState('');
  const [jerseyNumber, setJerseyNumber] = useState('');
  const [teamName, setTeamName] = useState('');
  const [season, setSeason] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !teamName.trim() || !season.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      const response = await api.post('/fan/players', {
        name: name.trim(),
        jersey_number: jerseyNumber.trim(),
        team_name: teamName.trim(),
        season: season.trim()
      });
      toast.success('Player created!');
      onPlayerAdded(response.data);
      setName(''); setJerseyNumber(''); setTeamName(''); setSeason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create player');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-player-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-bold">
            <UserPlus className="w-5 h-5" style={{ color: tc }} />
            Create Player Profile
          </DialogTitle>
          <DialogDescription className="text-slate-500">Add the athlete you want to track. They'll receive a unique Athlete ID.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div>
            <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">Player Name *</Label>
            <Input id="name" data-testid="player-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Jordan Smith" required />
          </div>
          <div>
            <Label htmlFor="jersey" className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">Jersey Number</Label>
            <Input id="jersey" data-testid="jersey-number-input" value={jerseyNumber} onChange={(e) => setJerseyNumber(e.target.value)} placeholder="e.g., 23" />
          </div>
          <div>
            <Label htmlFor="team" className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">Team Name *</Label>
            <Input id="team" data-testid="team-name-input" value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="e.g., Warriors U14" required />
          </div>
          <div>
            <Label htmlFor="season" className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">Season *</Label>
            <Input id="season" data-testid="season-input" value={season} onChange={(e) => setSeason(e.target.value)} placeholder="e.g., Spring 2026" required />
          </div>
          <Button type="submit" disabled={saving} data-testid="create-player-submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {saving ? 'Creating...' : 'Create Player'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NewGameModal({ open, onOpenChange, player, onGameCreated, themeColor }) {
  const tc = themeColor || '#7e22ce';
  const [opponentName, setOpponentName] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [isHome, setIsHome] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    setDateTime(now.toISOString().slice(0, 16));
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!opponentName.trim() || !dateTime) {
      toast.error('Please fill in all fields');
      return;
    }
    setSaving(true);
    try {
      const response = await api.post('/fan/games', {
        player_id: player.player_id,
        opponent_name: opponentName.trim(),
        date_time: dateTime,
        is_home: isHome
      });
      await api.post(`/fan/games/${response.data.game_id}/start`, {});
      toast.success('Game started!');
      onGameCreated(response.data);
      setOpponentName(''); setIsHome(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create game');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-game-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-bold">
            <Play className="w-5 h-5 text-blue-500" />
            New Game
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            {player ? `Tracking: ${player.name} (${player.team_name})` : 'Select a player first'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div>
            <Label htmlFor="opponent" className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">Opponent *</Label>
            <Input id="opponent" data-testid="opponent-name-input" value={opponentName} onChange={(e) => setOpponentName(e.target.value)} placeholder="e.g., Lakers" required />
          </div>
          <div>
            <Label htmlFor="datetime" className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">Date & Time</Label>
            <Input id="datetime" type="datetime-local" data-testid="game-datetime-input" value={dateTime} onChange={(e) => setDateTime(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">Location</Label>
            <div className="flex gap-2">
              <Button type="button" variant={isHome ? 'default' : 'outline'} onClick={() => setIsHome(true)} className={isHome ? 'bg-slate-900 hover:bg-slate-800 text-white flex-1' : 'flex-1'}>Home</Button>
              <Button type="button" variant={!isHome ? 'default' : 'outline'} onClick={() => setIsHome(false)} className={!isHome ? 'bg-slate-900 hover:bg-slate-800 text-white flex-1' : 'flex-1'}>Away</Button>
            </div>
          </div>
          <Button type="submit" disabled={saving || !player} data-testid="start-game-submit" className="w-full h-11 bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wide">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
            {saving ? 'Starting...' : 'Start Game'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function ManualGameSetupModal({ open, onOpenChange, player, themeColor }) {
  const tc = themeColor || '#7e22ce';
  const navigate = useNavigate();
  const [opponentName, setOpponentName] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [isHome, setIsHome] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    setDateTime(now.toISOString().slice(0, 16));
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!opponentName.trim() || !dateTime) {
      toast.error('Please fill in all fields');
      return;
    }
    setSaving(true);
    try {
      const response = await api.post('/fan/games/manual', {
        player_id: player.player_id,
        opponent_name: opponentName.trim(),
        date_time: dateTime,
        is_home: isHome
      });
      toast.success('Manual game created!');
      onOpenChange(false);
      setOpponentName(''); setIsHome(true);
      navigate(`/fan/manual-stats/${response.data.game_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create game');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="manual-game-setup-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-bold">
            <ClipboardList className="w-5 h-5 text-blue-500" />
            Add Stats (Manual)
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            {player ? `Enter game stats for: ${player.name} (${player.team_name})` : 'Select a player first'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div>
            <Label htmlFor="manual-opponent" className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">Opponent *</Label>
            <Input id="manual-opponent" data-testid="manual-opponent-input" value={opponentName} onChange={(e) => setOpponentName(e.target.value)} placeholder="e.g., Lakers" required />
          </div>
          <div>
            <Label htmlFor="manual-datetime" className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">Game Date & Time</Label>
            <Input id="manual-datetime" type="datetime-local" data-testid="manual-datetime-input" value={dateTime} onChange={(e) => setDateTime(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">Location</Label>
            <div className="flex gap-2">
              <Button type="button" variant={isHome ? 'default' : 'outline'} onClick={() => setIsHome(true)} className={isHome ? 'bg-slate-900 hover:bg-slate-800 text-white flex-1' : 'flex-1'}>Home</Button>
              <Button type="button" variant={!isHome ? 'default' : 'outline'} onClick={() => setIsHome(false)} className={!isHome ? 'bg-slate-900 hover:bg-slate-800 text-white flex-1' : 'flex-1'}>Away</Button>
            </div>
          </div>
          <Button type="submit" disabled={saving || !player} data-testid="manual-setup-submit" className="w-full h-11 bg-blue-500 hover:bg-blue-600 text-white font-bold uppercase tracking-wide">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ClipboardList className="w-4 h-4 mr-2" />}
            {saving ? 'Creating...' : 'Continue to Stats Entry'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}



function LinkPlayerModal({ open, onOpenChange, players, themeColor, onSuccess, allowDirectLink }) {
  const tc = themeColor || '#7e22ce';
  const [selectedSeat, setSelectedSeat] = useState('');
  const [athleteId, setAthleteId] = useState('');
  const [linking, setLinking] = useState(false);
  const hasSeats = players && players.length > 0;

  useEffect(() => {
    if (!open) {
      setSelectedSeat('');
      setAthleteId('');
    } else if (hasSeats && !selectedSeat) {
      setSelectedSeat(players[0].player_id);
    }
  }, [open, players]);

  const handleLink = async (e) => {
    e.preventDefault();
    if (!athleteId.trim()) {
      toast.error('Please enter an Athlete ID');
      return;
    }

    setLinking(true);
    try {
      if (hasSeats && selectedSeat) {
        // Link existing seat
        await api.post(`/fan/players/${selectedSeat}/link-athlete`, {
          athlete_id: athleteId.trim().toUpperCase()
        });
      } else {
        // No seats — create a new seat linked directly to the athlete_id
        await api.post('/fan/players/link-by-athlete-id', {
          athlete_id: athleteId.trim().toUpperCase()
        });
      }
      toast.success('Player account linked successfully!');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link player account');
    } finally {
      setLinking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="link-player-modal">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-xl">Link Existing Player Account</DialogTitle>
          <DialogDescription className="text-slate-600 mt-1">
            {hasSeats
              ? 'Connect one of your athlete seats to an existing Player account using their Athlete ID.'
              : "Enter your child's Athlete ID to link their account to your dashboard."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleLink} className="space-y-4 pt-2">
          {hasSeats && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">Select Athlete Seat</Label>
              <Select value={selectedSeat} onValueChange={setSelectedSeat}>
                <SelectTrigger data-testid="link-seat-selector" className="h-10 font-medium">
                  <SelectValue placeholder="Select player" />
                </SelectTrigger>
                <SelectContent>
                  {players.map((p) => (
                    <SelectItem key={p.player_id} value={p.player_id}>
                      {p.name} — {p.team_name} ({p.season})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">Athlete ID</Label>
            <Input
              value={athleteId}
              onChange={(e) => setAthleteId(e.target.value.toUpperCase())}
              placeholder="Enter Athlete ID (e.g., ABC1234XYZ)"
              maxLength={10}
              data-testid="link-athlete-id-input"
              className="h-12 text-center text-lg font-mono tracking-widest uppercase"
              required
            />
            <p className="text-xs text-slate-500 mt-1.5">Ask the player to share their Athlete ID from their Player dashboard or Settings.</p>
          </div>
          <Button
            type="submit"
            disabled={linking || !athleteId.trim() || (hasSeats && !selectedSeat)}
            data-testid="link-player-submit"
            className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide"
          >
            {linking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {linking ? 'Linking...' : 'Link Player Account'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AthleteSettingsModal({ open, onOpenChange, player, themeColor }) {
  const tc = themeColor || '#7e22ce';
  const [copiedId, setCopiedId] = useState(false);

  if (!player) return null;

  const copyAID = () => {
    navigator.clipboard.writeText(player.athlete_id || '');
    setCopiedId(true);
    toast.success('Athlete ID copied!');
    setTimeout(() => setCopiedId(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="athlete-settings-modal">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-xl flex items-center gap-2">
            <Settings className="w-5 h-5 text-slate-500" />
            Athlete Settings
          </DialogTitle>
          <DialogDescription className="text-slate-600 mt-1">
            Settings for {player.name}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 pt-2">
          {/* Athlete ID */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">Athlete ID</Label>
            <div className="flex items-center gap-2">
              <Input
                value={player.athlete_id || 'Not assigned'}
                disabled
                className="h-12 bg-slate-50 text-slate-700 font-mono tracking-wider text-lg"
                data-testid="athlete-settings-id-field"
              />
              {player.athlete_id && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 px-4"
                  onClick={copyAID}
                  data-testid="athlete-settings-copy-id"
                >
                  {copiedId ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1.5">You can share this with your athlete so they can link their own account.</p>
          </div>

          {/* Linked Player Info */}
          {player.linked_user_id && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800 flex items-center gap-2">
                <Check className="w-4 h-4 text-green-600" />
                <span>Linked to player account</span>
              </p>
            </div>
          )}

          {/* Player Details */}
          <div className="border-t border-slate-100 pt-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Name</span>
              <span className="font-medium text-slate-900">{player.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Team</span>
              <span className="font-medium text-slate-900">{player.team_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Season</span>
              <span className="font-medium text-slate-900">{player.season}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Jersey</span>
              <span className="font-medium text-slate-900">#{player.jersey_number || '--'}</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function JoinTeamModal({ open, onOpenChange, selectedPlayer, players, themeColor, onSuccess }) {
  const tc = themeColor || '#7e22ce';
  const [step, setStep] = useState('code'); // 'code' | 'roster' | 'fanProfile'
  const [joinCode, setJoinCode] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [team, setTeam] = useState(null);
  const [fanProfile, setFanProfile] = useState(null);
  const [unclaimedProfiles, setUnclaimedProfiles] = useState([]);
  const [selectedRosterProfile, setSelectedRosterProfile] = useState(null);
  const [claimingProfile, setClaimingProfile] = useState(false);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep('code');
      setJoinCode('');
      setTeam(null);
      setFanProfile(null);
      setUnclaimedProfiles([]);
      setSelectedRosterProfile(null);
    }
  }, [open]);

  const handleLookupCode = async (e) => {
    e.preventDefault();
    if (joinCode.length !== 8) {
      toast.error('Please enter an 8-character join code');
      return;
    }

    setLookupLoading(true);
    try {
      const response = await api.get(`/teams/lookup/${joinCode.toUpperCase()}`);
      const data = response.data;

      if (data.type === 'fan_player') {
        if (data.already_claimed) {
          toast.error('This profile has already been claimed.');
          return;
        }
        setFanProfile(data);
        setStep('fanProfile');
      } else if (data.type === 'team') {
        setTeam(data);
        const profilesResponse = await api.get(`/teams/${data.team_id}/unclaimed-profiles`);
        setUnclaimedProfiles(profilesResponse.data);
        setStep('roster');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Invalid join code. Please check and try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to lookup code');
      }
    } finally {
      setLookupLoading(false);
    }
  };

  const handleClaimAsParent = async () => {
    if (!selectedRosterProfile || !team || !selectedPlayer) return;

    setClaimingProfile(true);
    try {
      await api.post('/profiles/claim-as-parent', {
        player_id: selectedRosterProfile.player_id,
        team_id: team.team_id,
        fan_player_id: selectedPlayer.player_id
      });
      toast.success(`Successfully claimed ${selectedRosterProfile.name}'s profile for ${selectedPlayer.name}!`);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim profile');
    } finally {
      setClaimingProfile(false);
    }
  };

  const handleClaimFanProfile = async () => {
    if (!fanProfile) return;

    setClaimingProfile(true);
    try {
      await api.post('/profiles/claim-fan', {
        player_id: fanProfile.player_id
      });
      toast.success(`Successfully claimed profile! Stats from ${fanProfile.team_name} are now linked.`);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim profile');
    } finally {
      setClaimingProfile(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="join-team-modal">
        {step === 'code' && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading uppercase text-2xl text-center">Join a Team</DialogTitle>
              <DialogDescription className="text-slate-600 text-center mt-1">
                Enter a team join code to claim a roster spot for your player
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {/* Info banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800 flex items-center gap-2">
                  <Info className="w-4 h-4 flex-shrink-0" />
                  Players and fans can claim profiles
                </p>
              </div>
              <form onSubmit={handleLookupCode} className="space-y-4">
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">Join Code</Label>
                  <Input
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                    placeholder="Enter 8-character code"
                    maxLength={8}
                    data-testid="fan-join-code-input"
                    className="h-14 text-center text-2xl font-bold tracking-widest uppercase"
                    required
                  />
                  <p className="text-sm text-slate-500 mt-2 text-center">Ask the coach for the team join code</p>
                </div>
                <Button
                  type="submit"
                  disabled={lookupLoading || joinCode.length !== 8}
                  data-testid="fan-find-team-button"
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide"
                >
                  {lookupLoading ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Finding Team...
                    </span>
                  ) : 'Find Team'}
                </Button>
              </form>
            </div>
          </>
        )}

        {step === 'roster' && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading uppercase text-2xl text-center" data-testid="roster-modal-title">Select Your Profile</DialogTitle>
              <DialogDescription className="text-slate-600 text-center mt-1">
                {team ? `${team.name}${team.level ? ` • ${team.level}` : ''}` : 'Select a profile from the roster'}
              </DialogDescription>
            </DialogHeader>

            {/* Info banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 flex items-center gap-2">
                <Info className="w-4 h-4 flex-shrink-0" />
                Players and fans can claim profiles
              </p>
            </div>

            <div className="flex-1 overflow-y-auto py-2">
              {unclaimedProfiles.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-slate-900 mb-2">No Available Profiles</h3>
                  <p className="text-slate-600 text-sm">
                    All profiles on this team have been claimed, or the coach hasn't added players yet.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-slate-600 mb-3 text-center">Select your name from the roster below</p>
                  {unclaimedProfiles.map((profile) => (
                    <button
                      key={profile.player_id}
                      onClick={() => setSelectedRosterProfile(profile)}
                      data-testid={`roster-profile-${profile.player_id}`}
                      className={`w-full p-4 rounded-lg border-2 transition-all text-left flex items-center justify-between ${
                        selectedRosterProfile?.player_id === profile.player_id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                          selectedRosterProfile?.player_id === profile.player_id
                            ? 'bg-blue-600 text-white'
                            : 'bg-slate-100 text-slate-700'
                        }`}>
                          {profile.jersey_number || '#'}
                        </div>
                        <div>
                          <p className="font-bold text-lg text-slate-900">{profile.name}</p>
                          <p className="text-sm text-slate-500">Jersey #{profile.jersey_number || 'Not assigned'}</p>
                        </div>
                      </div>
                      {selectedRosterProfile?.player_id === profile.player_id && (
                        <Check className="w-6 h-6 text-blue-600" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {unclaimedProfiles.length > 0 && (
              <div className="pt-4 border-t border-slate-200 space-y-3">
                {/* Show which managed player this will link to */}
                {selectedPlayer && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm" style={{ backgroundColor: tc + '15', color: tc }}>
                      {selectedPlayer.jersey_number || '#'}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-bold">Claiming for</p>
                      <p className="text-sm font-semibold text-slate-900">{selectedPlayer.name} ({selectedPlayer.athlete_id})</p>
                    </div>
                  </div>
                )}
                <Button
                  onClick={handleClaimAsParent}
                  disabled={!selectedRosterProfile || claimingProfile}
                  data-testid="claim-profile-button"
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide disabled:opacity-50"
                >
                  {claimingProfile ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Claiming Profile...
                    </span>
                  ) : 'Claim This Profile'}
                </Button>
              </div>
            )}
          </>
        )}

        {step === 'fanProfile' && fanProfile && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading uppercase text-2xl text-center">Claim Profile</DialogTitle>
              <DialogDescription className="text-slate-600 text-center mt-1">
                A fan has been tracking stats for this player
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 text-center mb-6">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-1">{fanProfile.name}</h3>
                <p className="text-slate-600">{fanProfile.team_name}</p>
                <p className="text-sm text-slate-500">{fanProfile.season}</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-amber-800">
                  <strong>What happens when you claim:</strong><br />
                  All game stats tracked for this profile will be linked to your player's account.
                </p>
              </div>
              <Button
                onClick={handleClaimFanProfile}
                disabled={claimingProfile}
                data-testid="claim-fan-profile-button"
                className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white font-bold uppercase tracking-wide disabled:opacity-50"
              >
                {claimingProfile ? (
                  <span className="flex items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Claiming Profile...
                  </span>
                ) : 'Claim This Profile'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


function AllTimeStatsModal({ open, onOpenChange, stats, themeColor }) {
  const tc = themeColor || '#7e22ce';
  if (!stats) return null;

  const { totals = {}, averages = {}, games_played = 0, by_season = [] } = stats;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="all-time-stats-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-bold">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            All-Time Stats
          </DialogTitle>
          <DialogDescription className="text-slate-500">Career statistics across all teams and seasons ({games_played} games)</DialogDescription>
        </DialogHeader>
        <div className="space-y-6 pt-2">
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-3">Career Averages</h4>
            <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
              {[
                { label: 'PTS', value: averages.pts },
                { label: 'REB', value: averages.reb },
                { label: 'AST', value: averages.ast },
                { label: 'STL', value: averages.stl },
                { label: 'BLK', value: averages.blk },
                { label: '3PM', value: averages.three_pm },
                { label: 'TO', value: averages.to },
              ].map((s) => (
                <div key={s.label} className="text-center p-3 rounded-lg bg-slate-50">
                  <p className="text-lg font-bold text-slate-900">{s.value ?? '-'}</p>
                  <p className="text-[10px] text-slate-500 uppercase font-medium tracking-wider">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
          {games_played > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-3">Shooting</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-slate-900">{averages.fg_pct || 0}%</p>
                  <p className="text-[10px] text-slate-500 uppercase">FG%</p>
                  <p className="text-[10px] text-slate-400">{totals.fgm}-{totals.fga}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-slate-900">{averages.three_p_pct || 0}%</p>
                  <p className="text-[10px] text-slate-500 uppercase">3P%</p>
                  <p className="text-[10px] text-slate-400">{totals.three_pm}-{totals.three_pa}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-slate-900">{averages.ft_pct || 0}%</p>
                  <p className="text-[10px] text-slate-500 uppercase">FT%</p>
                  <p className="text-[10px] text-slate-400">{totals.ftm}-{totals.fta}</p>
                </div>
              </div>
            </div>
          )}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-3">Career Totals</h4>
            <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
              {[
                { label: 'PTS', value: totals.pts },
                { label: 'REB', value: totals.reb },
                { label: 'AST', value: totals.ast },
                { label: 'STL', value: totals.stl },
                { label: 'BLK', value: totals.blk },
                { label: '3PM', value: totals.three_pm },
                { label: 'TO', value: totals.to },
              ].map((s) => (
                <div key={s.label} className="text-center p-3 rounded-lg bg-slate-50">
                  <p className="text-lg font-bold text-slate-900">{s.value ?? '-'}</p>
                  <p className="text-[10px] text-slate-500 uppercase font-medium tracking-wider">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
          {by_season.length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-3">By Season ({by_season.length})</h4>
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                {by_season.map((s, idx) => (
                  <div key={idx} className={`p-3 ${s.is_archived ? 'bg-slate-50' : ''}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <span className="font-semibold text-slate-900">{s.team_name}</span>
                        <span className="text-slate-500 text-sm ml-2">{s.season}</span>
                      </div>
                      <span className="text-sm text-slate-500">{s.games_played} games</span>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <span><strong>{s.ppg}</strong> PPG</span>
                      <span><strong>{s.rpg}</strong> RPG</span>
                      <span><strong>{s.apg}</strong> APG</span>
                      <span className="text-slate-500">{s.record}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Button onClick={() => onOpenChange(false)} className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
